import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";
import api from "../../api";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, ChartTooltip, ChartLegend);

// Phase 52 (New Joiner Asset Request). Task spec §25-31: Device Deployment
// is a JOINING-DATE SLA (deployed_date vs. employee joining_date), a
// deliberately SEPARATE metric from the ticket-response/resolution TAT
// cards above in TechnicianPerformancePanel.jsx -- rendered as its own,
// clearly-labeled section rather than folded into the existing
// `tpp-kpi-grid` so the two SLA systems are never visually or numerically
// blended (task spec §29/§30: "do not mix Joining-Date SLA with
// request-response TAT").
export default function NewJoinerDeviceDeploymentCard() {
  const [kpi, setKpi] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/new-joiner-requests/dashboard/kpi"),
      api.get("/new-joiner-requests/dashboard/trend"),
    ])
      .then(([kpiRes, trendRes]) => {
        if (cancelled) return;
        setKpi(kpiRes.data?.data || null);
        setTrend(Array.isArray(trendRes.data?.data) ? trendRes.data.data : []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || "Failed to load device deployment metrics.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const trendChart = {
    labels: trend.map((t) => t.monthKey),
    datasets: [
      {
        label: "On-Time Deployment Rate (%)",
        data: trend.map((t) => t.deviceDeploymentOnTimeRate),
        borderColor: "#6d1530",
        backgroundColor: "#6d1530",
        tension: 0.3,
      },
    ],
  };
  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 10 }, padding: 6 } } },
    scales: {
      y: { beginAtZero: true, max: 100, ticks: { font: { size: 10 } } },
      x: { ticks: { font: { size: 10 } } },
    },
  };

  if (loading) {
    return (
      <div className="tpp-trend njdd-section">
        <div className="td-ch"><strong>Device Deployment</strong></div>
        <div className="td-loader"><p>Loading device deployment metrics…</p></div>
      </div>
    );
  }

  if (error || !kpi) {
    return (
      <div className="tpp-trend njdd-section">
        <div className="td-ch"><strong>Device Deployment</strong></div>
        <div className="td-empty"><p>{error || "No data available."}</p></div>
      </div>
    );
  }

  if (kpi.deployed === 0) {
    return (
      <div className="tpp-trend njdd-section">
        <div className="td-ch"><strong>Device Deployment</strong></div>
        <div className="td-empty"><p>No New Joiner deployments recorded yet.</p></div>
      </div>
    );
  }

  return (
    <div className="tpp-trend njdd-section">
      <div className="td-ch">
        <strong>Device Deployment</strong>
        <span className="njdd-subtitle">New Joiner joining-date SLA — separate from ticket response TAT</span>
      </div>
      <div className="njdd-kpi-grid">
        <div className="td-card njdd-kpi-card">
          <span className="njdd-kpi-title">On Time</span>
          <div className="njdd-kpi-value">{kpi.onTimeRate == null ? "—" : `${kpi.onTimeRate}%`}</div>
          <div className="njdd-kpi-sub">{kpi.deployed} deployed</div>
        </div>
        <div className="td-card njdd-kpi-card njdd-kpi-card--muted">
          <span className="njdd-kpi-title">On/Before Joining</span>
          <div className="njdd-kpi-value">{kpi.onTime}</div>
        </div>
        <div className="td-card njdd-kpi-card njdd-kpi-card--danger">
          <span className="njdd-kpi-title">Late</span>
          <div className="njdd-kpi-value">{kpi.late}</div>
        </div>
        <div className="td-card njdd-kpi-card njdd-kpi-card--amber">
          <span className="njdd-kpi-title">Pending Overdue</span>
          <div className="njdd-kpi-value">{kpi.pendingOverdue}</div>
        </div>
      </div>
      <div className="td-chartbox" style={{ height: 220 }}>
        {trend.some((t) => t.deployed > 0) ? (
          <Line data={trendChart} options={trendOptions} />
        ) : (
          <div className="td-empty"><p>No monthly trend data yet.</p></div>
        )}
      </div>
      <style>{`
        .njdd-section{margin-top:10px;}
        .njdd-subtitle{font-size:10.5px;color:#8b877f;font-weight:600;margin-left:10px;}
        .njdd-kpi-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;padding:16px;}
        .njdd-kpi-card{padding:14px 16px;box-shadow:none;border-radius:12px;min-height:80px;}
        .njdd-kpi-title{font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.05em;}
        .njdd-kpi-value{font-size:24px;font-weight:700;color:#6d1530;margin-top:6px;line-height:1.15;}
        .njdd-kpi-sub{font-size:11px;color:#64748b;font-weight:600;margin-top:2px;}
        .njdd-kpi-card--danger .njdd-kpi-value{color:#dc2626;}
        .njdd-kpi-card--amber .njdd-kpi-value{color:#d97706;}
        .njdd-kpi-card--muted .njdd-kpi-value{color:#16a34a;}
        @media(max-width:900px){.njdd-kpi-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}
      `}</style>
    </div>
  );
}
